import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Baby, 
  Activity, 
  Shield, 
  CheckCircle, 
  Clock, 
  Calendar,
  Heart,
  Eye,
  Stethoscope,
  Sparkles,
  CreditCard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { SEO } from '../components/SEO';

export const Packages = () => {
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  return (
    <div className="min-h-screen" data-testid="packages-page">
      <SEO 
        title="Healthcare Packages"
        description="Affordable healthcare packages at K&K Medical and Dental. Antenatal care, Diabetic management, and HIV monitoring packages designed to reduce costs and make healthcare accessible."
        keywords="antenatal package Umzinto, diabetic care package, HIV monitoring KZN, affordable healthcare packages, chronic care South Africa"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 py-16 lg:py-24 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 text-white text-sm font-semibold mb-6 animate-fade-in-up backdrop-blur-sm">
            <Sparkles className="w-4 h-4" />
            Special Healthcare Packages
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6 animate-fade-in-up animation-delay-100">
            Making Healthcare <br className="hidden sm:block" />
            <span className="text-emerald-200">Accessible to All</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/90 leading-relaxed max-w-3xl mx-auto mb-8 animate-fade-in-up animation-delay-200">
            Our carefully designed packages help reduce the cost and burden of healthcare, 
            ensuring comprehensive care without compromising quality.
          </p>
          
          {/* Package Quick Links */}
          <div className="flex flex-wrap justify-center gap-4 animate-fade-in-up animation-delay-300">
            <a 
              href="#antenatal" 
              className="inline-flex items-center gap-2 bg-white text-teal-700 px-6 py-3 rounded-full font-semibold hover:bg-teal-50 transition-all duration-300 shadow-lg hover:shadow-xl"
              data-testid="quick-link-antenatal"
            >
              <Baby className="w-5 h-5" />
              Antenatal
            </a>
            <a 
              href="#diabetic" 
              className="inline-flex items-center gap-2 bg-white text-teal-700 px-6 py-3 rounded-full font-semibold hover:bg-teal-50 transition-all duration-300 shadow-lg hover:shadow-xl"
              data-testid="quick-link-diabetic"
            >
              <Activity className="w-5 h-5" />
              Diabetic
            </a>
            <a 
              href="#hiv" 
              className="inline-flex items-center gap-2 bg-white text-teal-700 px-6 py-3 rounded-full font-semibold hover:bg-teal-50 transition-all duration-300 shadow-lg hover:shadow-xl"
              data-testid="quick-link-hiv"
            >
              <Shield className="w-5 h-5" />
              HIV Monitoring
            </a>
          </div>
        </div>
      </section>

      {/* Antenatal Package Section */}
      <section id="antenatal" className="py-16 lg:py-24 bg-gradient-to-br from-pink-50 to-rose-50 scroll-mt-24" data-testid="antenatal-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-100 rounded-2xl mb-6">
                <Baby className="w-8 h-8 text-pink-600" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
                Antenatal Package
              </h2>
              <div className="flex items-baseline gap-3 mb-6">
                <span className="text-2xl font-bold text-pink-600">Get a personalised quote</span>
              </div>
              <p className="text-sm text-pink-600 font-medium mb-4">One upfront payment • 4 antenatal visits included</p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Comprehensive prenatal care to support you and your baby throughout your pregnancy journey. 
                Our package includes everything you need for peace of mind.
              </p>
              
              {/* Inclusions */}
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm">
                  <CheckCircle className="w-6 h-6 text-pink-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-slate-900">4 Ultrasound Scans</h4>
                    <p className="text-slate-600 text-sm">At any time during pregnancy, including pictures of your baby</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm">
                  <CheckCircle className="w-6 h-6 text-pink-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-slate-900">Urine Tests & Vitals</h4>
                    <p className="text-slate-600 text-sm">Regular monitoring of your health throughout pregnancy</p>
                  </div>
                </div>
              </div>

              <Button 
                asChild
                className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="antenatal-book-btn"
              >
                <Link to="/booking#antenatal">Book Antenatal Package</Link>
              </Button>
            </div>

            {/* Recommended Scan Times Card */}
            <div className="animate-fade-in-up animation-delay-200">
              <Card className="border-0 shadow-xl rounded-3xl overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-pink-500 to-rose-500 text-white py-6">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-6 h-6" />
                    <CardTitle className="text-xl text-white">Recommended Scan Times</CardTitle>
                  </div>
                  <p className="text-pink-100 text-sm mt-2">
                    Scans can be performed at any time, but these are optimal timing recommendations
                  </p>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-slate-100">
                    <div className="flex items-center gap-4 p-5 hover:bg-pink-50 transition-colors duration-200">
                      <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-pink-600 font-bold">1</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Dating Scan</h4>
                        <p className="text-slate-500 text-sm">11 - 13 weeks</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-5 hover:bg-pink-50 transition-colors duration-200">
                      <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-pink-600 font-bold">2-3</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Growth Scans</h4>
                        <p className="text-slate-500 text-sm">28 - 36 weeks (2 scans)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-5 hover:bg-pink-50 transition-colors duration-200">
                      <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-pink-600 font-bold">4</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Late Pregnancy Scan</h4>
                        <p className="text-slate-500 text-sm">36 - 40 weeks</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Diabetic Package Section */}
      <section id="diabetic" className="py-16 lg:py-24 bg-gradient-to-br from-blue-50 to-indigo-50 scroll-mt-24" data-testid="diabetic-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12 animate-fade-in-up">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-6">
              <Activity className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Diabetic Chronic Management Package
            </h2>
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="text-2xl font-bold text-blue-600">Get a personalised quote</span>
            </div>
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <CreditCard className="w-4 h-4" />
              Payment as a once-off or over 2 months
            </div>
            <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
              Save money and time with comprehensive diabetic care that covers every aspect of your condition. 
              Our proactive approach allows us to respond swiftly to changes and <span className="font-semibold text-blue-700">prevent serious complications before they develop</span>.
            </p>
          </div>

          {/* Complications Prevention Banner */}
          <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl p-6 mb-12 text-white animate-fade-in-up animation-delay-100">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="bg-white/20 rounded-xl p-4 flex-shrink-0">
                <Heart className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Prevention is Better Than Cure</h3>
                <p className="text-white/90">
                  Proper management helps protect you from serious complications including <span className="font-semibold">Heart Disease, Stroke, Gangrene, 
                  Kidney Disease, Eye Disease, and Erectile Dysfunction</span>.
                </p>
              </div>
            </div>
          </div>

          {/* Visit Schedule Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in-up animation-delay-200">
            
            {/* Every Visit Card */}
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden bg-white hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-5">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5" />
                  <CardTitle className="text-lg text-white">Every Visit</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-5">
                <ul className="space-y-3">
                  {['Weight & waist circumference', 'Blood pressure check', 'Feet examination', 'Other associated complications (e.g., ED, fungal infections)', 'Focus on prevention of macro- and microvascular complications'].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* First Visit Card */}
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden bg-white hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white py-5">
                <div className="flex items-center gap-3">
                  <Stethoscope className="w-5 h-5" />
                  <CardTitle className="text-lg text-white">1st Visit</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-5">
                <ul className="space-y-3">
                  {[
                    'Consultation + Blood tests', 
                    'HBA1c + Creatinine', 
                    'Urine microalbumin + Full Lipogram', 
                    'Vitamin B12', 
                    'Colon cancer screening/Pap smear (if indicated)',
                    'Weight & waist circumference',
                    'Referral to dietician'
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Every 3 Months Card */}
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden bg-white hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="bg-gradient-to-r from-purple-500 to-purple-600 text-white py-5">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5" />
                  <CardTitle className="text-lg text-white">Every 3 Months</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-5">
                <ul className="space-y-3">
                  {['Consultation', 'HBA1c blood test', 'Weight & waist circumference'].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* 6 Monthly Card */}
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden bg-white hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="bg-gradient-to-r from-teal-500 to-teal-600 text-white py-5">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5" />
                  <CardTitle className="text-lg text-white">6 Monthly</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-5">
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
                    Referral to dentist (discounted rates)
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Yearly Card */}
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden bg-white hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white py-5">
                <div className="flex items-center gap-3">
                  <Eye className="w-5 h-5" />
                  <CardTitle className="text-lg text-white">Yearly</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-5">
                <ul className="space-y-3">
                  {[
                    'ECG + Cardiovascular risk stratification', 
                    'Fundoscopy (in office)', 
                    'Bowel cancer screening (above age 45)', 
                    'Referral to ophthalmologist for retinal imaging',
                    'Referral for mammogram (above age 40)'
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Every 3 Years Card */}
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden bg-white hover:shadow-xl transition-shadow duration-300">
              <CardHeader className="bg-gradient-to-r from-rose-500 to-rose-600 text-white py-5">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5" />
                  <CardTitle className="text-lg text-white">Every 3 Years</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-5">
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
                    Pap smear (women age 21 to 65)
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Book CTA */}
          <div className="text-center mt-12 animate-fade-in-up animation-delay-300">
            <Button 
              asChild
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-300"
              data-testid="diabetic-book-btn"
            >
              <Link to="/booking#diabetic">Book Diabetic Package</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* HIV Monitoring Package Section */}
      <section id="hiv" className="py-16 lg:py-24 bg-gradient-to-br from-violet-50 to-purple-50 scroll-mt-24" data-testid="hiv-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-violet-100 rounded-2xl mb-6">
                <Shield className="w-8 h-8 text-violet-600" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
                HIV Monitoring & Management Package
              </h2>
              <p className="text-slate-500 text-lg mb-2">Cash Patients</p>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-2xl font-bold text-violet-600">Get a personalised quote</span>
              </div>
              <p className="text-sm font-medium text-violet-600 mb-6">Requires 2 visits per year</p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Comprehensive HIV care including prescriptions and management of other associated conditions. 
                All essential blood tests are included in this package price.
              </p>

              <Button 
                asChild
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="hiv-book-btn"
              >
                <Link to="/booking#hiv">Book HIV Package</Link>
              </Button>
            </div>

            {/* Blood Tests Included Card */}
            <div className="animate-fade-in-up animation-delay-200">
              <Card className="border-0 shadow-xl rounded-3xl overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-violet-500 to-purple-600 text-white py-6">
                  <div className="flex items-center gap-3">
                    <Activity className="w-6 h-6" />
                    <CardTitle className="text-xl text-white">Bloods Included</CardTitle>
                  </div>
                  <p className="text-violet-100 text-sm mt-2">
                    All essential tests included in your package price
                  </p>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { name: 'CD4 Count', desc: 'Immune function' },
                      { name: 'Viral Load', desc: 'Treatment efficacy' },
                      { name: 'ALT', desc: 'Liver function' },
                      { name: 'Creatinine', desc: 'Kidney function' },
                      { name: 'Glucose', desc: 'Blood sugar' },
                      { name: 'Cholesterol', desc: 'Heart health' },
                    ].map((test) => (
                      <div key={test.name} className="flex items-center gap-3 p-3 bg-violet-50 rounded-xl">
                        <CheckCircle className="w-5 h-5 text-violet-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 text-sm whitespace-nowrap">{test.name}</p>
                          <p className="text-violet-600 text-xs">{test.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-violet-100 rounded-xl">
                    <p className="text-violet-800 text-sm font-medium flex items-center gap-2">
                      <Stethoscope className="w-4 h-4" />
                      Includes management of associated conditions
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-slate-900 to-slate-800" data-testid="packages-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Start Your Healthcare Journey?
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            Contact us to learn more about our packages or book your first appointment today.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              asChild
              className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              data-testid="packages-book-btn"
            >
              <Link to="/booking">Book Appointment</Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg transition-all duration-300"
              data-testid="packages-contact-btn"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
;
